import React, { useState, useRef } from "react";
import { useDispatch } from "react-redux";
import moment from "moment";
import { DocumentType } from "src/utils/enum";
import { openDocumentDetail } from "../../../../../../utils/documentModal";
import {
  getName,
  getDocumentLabelWithNumber,
  getFolioDetalle,
} from "./matchRowHelpers";
import { Icon, Popup } from "semantic-ui-react";
import { formatMoneyWithCurrency } from "../../../../../../utils/formatter";
import { importAlertState } from "../../../../../../redux/actions/alert";
import { importOpenSidebar } from "../../../../../../redux/actions/openSidebar";
import { importUpdateTransactions } from "../../../../../../redux/actions/conciliacion";
import { setUpdateDocuments } from "../../../../../../redux/actions/documentostributarios";
import { useSelector } from "react-redux";
import { ModifyDocument } from "../../../../../../graphql/documents";
import { RejectMatchRecommendation, MarkRecommendationAppliedByObligation } from "../../../../../../graphql/matchRecommender";
import { dt } from "../../../../../../utils/documentsI18n";
import { useModuleTcStatementPicker, isModuleTcObligationMeta } from "../hooks/useModuleTcStatementPicker";
import ModuleTcStatementPickerModal from "./ModuleTcStatementPickerModal";
import "./WeakCandidate.scss";
import { mergeTransactionMatchsPreserveEntries } from "../../../../../../utils/mergeTransactionMatchs";
import { dispatchRefreshNominaLists } from "src/utils/dispatchRefreshNominaLists";
import ModalNominaImputation, {
  NominaImputationMode,
  getImputableSelections,
} from "../ModalNominaImputation";

/**
 * WeakCandidate - Component for displaying weak match candidates
 * Similar to StrongCandidate but with light blue styling
 */
const WeakCandidate = ({ 
  candidate, 
  index, 
  matchContainer, 
  editData, 
  history, 
  location,
  generateMatch,
  generateMatchFetching,
  newAmount,
  newCurrency,
  showAmountComparison = false,
  modifyDocument,
  companyId,
  onReconcileSuccess,
  readOnly = false
}) => {
  const dispatch = useDispatch();
  const { openSidebar } = useSelector((state) => state.openSidebar);
  const { selectedCompany } = useSelector((state) => state.companyList);
  const [isHovered, setIsHovered] = useState(false);
  const [isReconciling, setIsReconciling] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [showNominaModal, setShowNominaModal] = useState(false);
  const [pendingMatchInput, setPendingMatchInput] = useState(null);
  const reconcileClickedRef = useRef(false);
  const pickerCompanyId = companyId || editData?.companyId || candidate?.companyId;
  const tcPicker = useModuleTcStatementPicker(pickerCompanyId);

  // Mutation to hide AI suggestion (use own mutation if modifyDocument prop not provided)
  const { executeMutation: hideDocument } = ModifyDocument();
  // Mutation to reject the AI recommendation in the training zone
  const { executeMutation: rejectRecommendation } = RejectMatchRecommendation();
  // Mutation to mark the AI recommendation as applied when the obligation is reconciled manually
  const { executeMutation: markRecommendationAppliedByObligation } = MarkRecommendationAppliedByObligation();

  // Check if this is an AI suggestion
  // Multiple ways to detect: source field, meta.isAISuggestion, or meta.suggestedForTransactionId
  const isAISuggestion = 
    candidate.source === 'MATCH_RECOMMENDER' || 
    candidate.meta?.isAISuggestion === true ||
    candidate.meta?.suggestedForTransactionId != null;

  // Use helper functions to extract match data
  const name = getName(candidate);
  const documentLabelWithNumber = getDocumentLabelWithNumber(candidate);
  const folioDetalle = getFolioDetalle(candidate);

  // Handler to open document detail modal
  const handleDocumentClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (candidate.id && [DocumentType.TAX_DOCUMENT, DocumentType.OBLIGATION, DocumentType.CREDIT].includes(candidate.documentType)) {
      openDocumentDetail(candidate.id, history, location);
    }
  };

  // Get match amount and currency
  // PRIORITY: Use remaining first (available amount for matching), then fallback to amount
  const matchKey = candidate.id || candidate.docId || candidate.settleId;
  const amount = matchContainer[matchKey + "matchedAmount"] || matchContainer[matchKey] || candidate.meta?.remaining || candidate.remaining || candidate.meta?.amount || candidate.amount || 0;
  const currency = matchContainer[matchKey + "currency"] || candidate.currencyType || candidate.currency || candidate.meta?.currency || 'CLP';
  const matchDate = moment(candidate.documentDate || candidate.date);
  
  // Calculate current amount for comparison (use remaining first, then fallback to amount)
  const currentAmount = candidate.meta?.remaining || candidate.remaining || candidate.meta?.amount || candidate.amount || 0;
  const currentCurrency = candidate.meta?.currency || candidate.currencyType || candidate.currency || 'CLP';
  
  // Check if we should show amount comparison
  const shouldShowComparison = showAmountComparison && 
    newAmount !== undefined && 
    newAmount !== null && 
    Math.abs(currentAmount - newAmount) > 0.01; // Allow small floating point differences
  
  const isClickable = [DocumentType.TAX_DOCUMENT, DocumentType.OBLIGATION, DocumentType.CREDIT].includes(candidate.documentType);
  
  // Check if any candidate is currently reconciling (global state) or if readOnly mode
  const isDisabled = isReconciling || generateMatchFetching || readOnly;

  // Generate avatar initials
  const getAvatarInitials = () => {
    if (documentLabelWithNumber) {
      const words = documentLabelWithNumber.split(' ');
      if (words.length >= 2) {
        return (words[0][0] + words[1][0]).toUpperCase();
      }
      return documentLabelWithNumber.substring(0, 2).toUpperCase();
    }
    return 'WC'; // Default: Weak Candidate
  };

  // Format amount with parentheses for red/received amounts
  const formatAmount = (amount, currency, isReceived) => {
    const formatted = formatMoneyWithCurrency(Math.abs(amount), currency);
    // If it's a received/red amount, wrap in parentheses
    if (isReceived) {
      return `(${formatted})`;
    }
    return formatted;
  };

  const executeMatchRequest = async (input) => {
    setIsReconciling(true);
    try {
      const { data: response, error } = await generateMatch({ input });

      if (error) {
        const errorMessage = error.graphQLErrors?.[0]?.message || error.message || "Hubo un problema al conciliar el documento.";
        const isPeriodLocked = errorMessage && typeof errorMessage === 'string' && 
          errorMessage.toLowerCase().includes('período') && 
          errorMessage.toLowerCase().includes('cerrado');
        dispatch(
          importAlertState({
            concept: isPeriodLocked ? "Período Cerrado" : undefined,
            content: errorMessage,
            state: isPeriodLocked ? "warning" : "error",
            duration: isPeriodLocked ? 6 : 4,
          })
        );
        return;
      }

      // Si la conciliación fue exitosa y el candidato proviene de IA, marcar
      // la recomendación como aplicada usando el id de la obligación.
      if (isAISuggestion && candidate.id) {
        try {
          await markRecommendationAppliedByObligation({ obligationId: candidate.id });
        } catch (markError) {
          console.warn('[WeakCandidate] Error marking recommendation as applied:', markError);
          // No bloquear la conciliación si falla este paso auxiliar
        }
      }

      // Update Redux with new transaction data
      if (response?.generateMatch?.data) {
        const type = response.generateMatch.data["__typename"];

        if (type === "Transaction") {
          const { remaining, matchs } = response.generateMatch.data;
          const newEditData = {
            ...editData,
            ...{
              remaining,
              matchs: mergeTransactionMatchsPreserveEntries(editData.matchs, matchs),
            },
          };

          dispatch(
            importOpenSidebar({
              ...openSidebar,
              editData: newEditData,
            })
          );
        }

        // Trigger refetch to update all data
        dispatch(importUpdateTransactions(true));
        if (editData.documentType) {
          dispatch(setUpdateDocuments({ value: true }));
        }
        if (input?.nominaId || input?.selectionId || input?.imputationMode) {
          dispatchRefreshNominaLists(dispatch);
        }

        // Call onReconcileSuccess callback if provided (e.g., to close modal)
        if (onReconcileSuccess) {
          onReconcileSuccess();
        }
      }
    } catch (error) {
      const errorMessage = error?.message || "Hubo un problema al conciliar el documento.";
      const isPeriodLocked = errorMessage && typeof errorMessage === 'string' && 
        errorMessage.toLowerCase().includes('período') && 
        errorMessage.toLowerCase().includes('cerrado');
      dispatch(
        importAlertState({
          concept: isPeriodLocked ? "Período Cerrado" : undefined,
          content: errorMessage,
          state: isPeriodLocked ? "warning" : "error",
          duration: isPeriodLocked ? 6 : 4,
        })
      );
    } finally {
      setIsReconciling(false);
      reconcileClickedRef.current = false;
    }
  };

  const executeMatch = async (input) => {
    const obligationMeta = [candidate?.meta, editData?.meta].find(isModuleTcObligationMeta);
    const { input: next, prompted } = await tcPicker.attachOrPrompt(
      input,
      obligationMeta
    );
    if (prompted) {
      setIsReconciling(false);
      reconcileClickedRef.current = false;
      return;
    }
    return executeMatchRequest(next);
  };

  const handleModuleTcPickerConfirm = async (statementId) => {
    const next = tcPicker.confirm(statementId);
    if (next) await executeMatchRequest(next);
  };

  const handleNominaConfirm = ({ imputationMode, nominaId, selectionId }) => {
    setShowNominaModal(false);
    if (!pendingMatchInput) return;
    const enrichedInput = {
      ...pendingMatchInput,
      imputationMode: imputationMode || NominaImputationMode.NEW_NOMINA,
    };
    if (nominaId) enrichedInput.nominaId = nominaId;
    if (selectionId) enrichedInput.selectionId = selectionId;
    setPendingMatchInput(null);
    executeMatch(enrichedInput);
  };

  // Handler for reconcile/match
  const handleReconcile = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (reconcileClickedRef.current || isDisabled || !editData || !candidate) return;
    reconcileClickedRef.current = true;

    setIsReconciling(true);

    try {
      // If we need to update amounts and modifyDocument is provided, update the document first
      // PRIORITY: Use remaining first (available amount for matching), then fallback to amount
      let finalAmount = candidate.meta?.remaining || candidate.remaining || candidate.meta?.amount || candidate.amount || amount;
      
      // Only update document if amounts are different and modifyDocument is available
      if (showAmountComparison && newAmount !== undefined && newAmount !== null && modifyDocument && companyId && candidate.id) {
        // Get current amount from the candidate
        const currentAmount = candidate.meta?.amount || candidate.amount || 0;
        
        // Check if amounts are different (with tolerance for floating point precision)
        const amountsAreDifferent = Math.abs(currentAmount - newAmount) > 0.01;
        
        // Only make API call if amounts are actually different
        if (amountsAreDifferent) {
          try {
            const fields = [
              { key: "amount", value: newAmount },
              { key: "meta.amount", value: newAmount },
              { key: "meta.remaining", value: newAmount },
              { key: "meta.originalAmount", value: newAmount },
            ];

            const { error: modifyError } = await modifyDocument({
              companyId: companyId,
              input: [
                { id: candidate.id, fields }
              ],
            });

            if (modifyError) {
              dispatch(
                importAlertState({
                  content: `Hubo un problema al actualizar el monto del documento.`,
                  state: "error",
                  duration: 4,
                })
              );
              setIsReconciling(false);
              return;
            }

            // Use the new amount for the match
            finalAmount = newAmount;
          } catch (modifyErr) {
            dispatch(
              importAlertState({
                content: `Hubo un problema al actualizar el monto del documento.`,
                state: "error",
                duration: 4,
              })
            );
            setIsReconciling(false);
            return;
          }
        } else {
          // Amounts are the same, use current amount for match
          finalAmount = currentAmount;
        }
      }

      // IMPORTANT: When matching from TRANSACTION to document, ensure amount doesn't exceed transaction remaining
      // This allows partial matches when document amount > transaction remaining
      // Use Math.abs to handle negative values (for RECEIVED transactions)
      // For documents, remaining is in meta.remaining
      const editDataIsDocument = !!editData.documentType;
      const transactionRemaining = editDataIsDocument
        ? Math.abs(editData.meta?.remaining !== undefined && editData.meta?.remaining !== null ? editData.meta.remaining : (editData.amount || 0))
        : Math.abs(editData.remaining !== undefined && editData.remaining !== null ? editData.remaining : 0);
      const documentAmount = Math.abs(finalAmount);
      
      // Cap the match amount at transaction remaining to allow partial matches
      // When document amount > transaction remaining, use transaction remaining (partial match)
      const matchAmount = transactionRemaining > 0 
        ? Math.min(documentAmount, transactionRemaining)
        : documentAmount;

      // Transaction is origin, document is end
      // IMPORTANT: Weak candidates are manually selected by user, so this is a manual match
      // However, if it was suggested by AI/heuristic, we track it as CONFIRMED_AI_RECOMMENDATION
      // Para sugerencias de IA queremos rastrear que provienen del sistema,
      // pero manteniendo la acción como manual a nivel de UI. Usamos CONFIRMED_AI_RECOMMENDATION
      // para que analytics pueda distinguir estos casos.
      const matchSourceValue = isAISuggestion ? 'CONFIRMED_AI_RECOMMENDATION' : 'MANUAL';
      
      // Determine origin/end types based on editData type
      const editDataIsRendicion = editDataIsDocument && editData.documentType === 'OBLIGATION';

      let input;
      if (editDataIsRendicion) {
        // Obligaciones sidebar: editData es Rendición (OBLIGATION), candidate es Factura (TAX_DOCUMENT)
        input = {
          originType: "OBLIGATION",
          originId: editData._id || editData.id,
          endType: candidate.documentType || "TAX_DOCUMENT",
          endId: candidate._id || candidate.id || candidate.docId,
          amount: matchAmount,
          originalAmount: matchAmount,
          accountPlan: "NOPLAN",
          companyId: editData.companyId || candidate.companyId,
          paymentDate: candidate.documentDate || editData.documentDate || new Date(),
          currency: currency || 'CLP',
          obligationType: editData.meta?.typeLabel || editData.meta?.type || '',
          matchSource: matchSourceValue,
        };
      } else if (editDataIsDocument) {
        // Document sidebar: candidate es Rendición (OBLIGATION), editData es TAX_DOCUMENT
        input = {
          originType: candidate.documentType || "OBLIGATION",
          originId: candidate.id || candidate.docId || candidate._id,
          endType: editData.documentType || "TAX_DOCUMENT",
          endId: editData._id || editData.id,
          amount: matchAmount,
          originalAmount: matchAmount,
          accountPlan: "NOPLAN",
          companyId: editData.companyId || candidate.companyId,
          paymentDate: editData.documentDate || new Date(),
          currency: currency || 'CLP',
          obligationType: candidate.meta?.typeLabel || candidate.meta?.type || '',
          matchSource: matchSourceValue,
        };
      } else {
        // Transaction sidebar: candidate es documento, editData es TRANSACTION
        input = {
          originType: "TRANSACTION",
          originId: editData.id || editData._id,
          endType: candidate.documentType || "TAX_DOCUMENT",
          endId: candidate.id || candidate.docId,
          amount: matchAmount,
          originalAmount: matchAmount,
          accountPlan: "NOPLAN",
          companyId: editData.companyId || candidate.companyId,
          paymentDate: editData.date || new Date(),
          currency: currency || 'CLP',
          obligationType: candidate.meta?.typeLabel || '',
          matchSource: matchSourceValue,
        };
      }

      // IMPORTANT: Remove 'origin' field if it exists - it's not part of GenerateMatchInput schema
      delete input.origin;

      // Pre-match nomina check: if the document has active nomina
      // selections, ask the user before applying the match.
      const totalUsed = Number(candidate?.meta?.nominaStatus?.totalUsed ?? 0)
      const imputableSelections = getImputableSelections(candidate?.meta?.nominaStatus)
      if (totalUsed > 0 && imputableSelections.length > 0) {
        setPendingMatchInput(input)
        setShowNominaModal(true)
        setIsReconciling(false)
        reconcileClickedRef.current = false
        return
      }

      await executeMatch(input)
    } catch (error) {
      const errorMessage = error?.message || "Hubo un problema al conciliar el documento.";
      const isPeriodLocked = errorMessage && typeof errorMessage === 'string' && 
        errorMessage.toLowerCase().includes('período') && 
        errorMessage.toLowerCase().includes('cerrado');
      dispatch(
        importAlertState({
          concept: isPeriodLocked ? "Período Cerrado" : undefined,
          content: errorMessage,
          state: isPeriodLocked ? "warning" : "error",
          duration: isPeriodLocked ? 6 : 4,
        })
      );
      setIsReconciling(false);
      reconcileClickedRef.current = false;
    }
  };

  // Handler to reject AI suggestion (hide the obligation and reject the recommendation)
  const handleRejectAISuggestion = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!isAISuggestion || !candidate.id) return;
    
    setIsRejecting(true);
    try {
      // 1. Hide the obligation document
      const mutationFn = modifyDocument || hideDocument;
      const { error } = await mutationFn({
        companyId: selectedCompany?.id || editData?.companyId,
        input: [{
          id: candidate.id,
          fields: [
            { key: 'isHidden', value: true },
          ],
        }],
      });
      
      // 2. Also reject the recommendation in the training zone (if we have the recommendation ID)
      if (candidate.aiRecommendationId && !String(candidate.aiRecommendationId).startsWith('virtual_')) {
        try {
          await rejectRecommendation({
            input: {
              recommendation_id: candidate.aiRecommendationId,
            },
          });
        } catch (rejectError) {
          console.warn('[WeakCandidate] Error rejecting recommendation:', rejectError);
          // Don't fail the whole operation if this fails
        }
      } else if (candidate.aiRecommendationId) {
        console.warn('[WeakCandidate] Skipping rejectMatchRecommendation for virtual suggestion id:', candidate.aiRecommendationId);
      }
      
      if (error) {
        dispatch(
          importAlertState({
            content: `Hubo un problema al rechazar la sugerencia.`,
            state: "error",
            duration: 4,
          })
        );
      } else {
        dispatch(
          importAlertState({
            content: `Sugerencia de IA rechazada.`,
            state: "success",
            duration: 3,
          })
        );
        // Trigger refetch to update all data
        dispatch(importUpdateTransactions(true));
      }
    } catch (error) {
      dispatch(
        importAlertState({
          content: `Hubo un problema al rechazar la sugerencia.`,
          state: "error",
          duration: 4,
        })
      );
    } finally {
      setIsRejecting(false);
    }
  };

  return (
    <>
      <div className={`weak-candidate-container ${isDisabled ? 'weak-candidate-container--disabled' : ''} ${isAISuggestion ? 'weak-candidate-container--ai' : ''}`}>
        <div className={`weak-candidate-label ${isAISuggestion ? 'weak-candidate-label--ai' : ''}`}>
          {isAISuggestion ? (
            <>
              Recomendación IA
              <Popup
                trigger={
                  <Icon
                    name="info circle"
                    size="small"
                    className="weak-candidate-label-icon"
                  />
                }
                content="Esta obligación fue creada automáticamente por IA basándose en patrones de conciliaciones anteriores. Puedes aprobarla haciendo click o rechazarla."
                inverted
                position="top center"
                size="tiny"
              />
            </>
          ) : (
            <>
              Candidato débil
              <Popup
                trigger={
                  <Icon
                    name="info circle"
                    size="small"
                    className="weak-candidate-label-icon"
                  />
                }
                content="Ciertas características como el monto y el emisor/receptor coinciden con el movimiento"
                inverted
                position="top center"
                size="tiny"
              />
            </>
          )}
        </div>
        <div 
          className="weak-candidate-item"
          onMouseEnter={() => !isDisabled && setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          onClick={handleReconcile}
        >
          <div className="weak-candidate-avatar">
            {getAvatarInitials()}
          </div>
          <div className="weak-candidate-body">
            <div className="weak-candidate-header">
              <div className={`weak-candidate-user ${isClickable ? 'weak-candidate-user--clickable' : ''} ${isAISuggestion ? 'weak-candidate-user--ai' : ''}`}>
                {isClickable ? (
                  <span onClick={handleDocumentClick}>
                    {documentLabelWithNumber}
                  </span>
                ) : (
                  documentLabelWithNumber
                )}
                {folioDetalle}
              </div>
              <div className="weak-candidate-time-container">
                <div className="weak-candidate-time">
                  {dt('reconcile.issuedOn')} {matchDate.format('DD/MM/YY')}
                </div>
                {isReconciling ? (
                  <div className="weak-candidate-reconcile-hint weak-candidate-reconcile-hint--spinner">
                    <div className="weak-candidate-small-spinner"></div>
                  </div>
                ) : isHovered && !isDisabled && (
                  <div className="weak-candidate-reconcile-hint">
                    <span className="weak-candidate-reconcile-text">{dt('reconcile.reconcile')}</span>
                    <Icon
                      name="arrow up right"
                      className="weak-candidate-reconcile-icon"
                      style={{ transform: 'rotate(45deg)' }}
                    />
                  </div>
                )}
              </div>
              <div className={`weak-candidate-amount-container ${candidate.meta?.isReceived ? 'weak-candidate-amount-container--received' : 'weak-candidate-amount-container--given'}`} style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: '8px' }}>
                {shouldShowComparison ? (
                  <>
                    <div style={{ 
                      position: 'absolute',
                      top: '-16px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      fontSize: '11px', 
                      color: '#6B7280', 
                      fontWeight: '500',
                      whiteSpace: 'nowrap'
                    }}>
                      Ajustaremos el monto:
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <span className="weak-candidate-amount-text">
                        {formatAmount(currentAmount, currentCurrency, candidate.meta?.isReceived)}
                      </span>
                      <i className="arrow right icon" style={{ 
                        margin: '0 6px', 
                        fontSize: '10px', 
                        color: '#6B7280',
                        fontWeight: '300',
                        opacity: 0.7
                      }}></i>
                      <span className="weak-candidate-amount-text">
                        {formatAmount(newAmount, newCurrency || currentCurrency, candidate.meta?.isReceived)}
                      </span>
                    </div>
                  </>
                ) : (
                  <span className="weak-candidate-amount-text">
                    {formatAmount(amount, currency, candidate.meta?.isReceived)}
                  </span>
                )}
                {/* AI Suggestion reject button - inline with amount */}
                {isAISuggestion && !readOnly && (
                  <Popup
                    trigger={
                      <div 
                        className="weak-candidate-reject-btn weak-candidate-reject-btn--inline"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRejectAISuggestion(e);
                        }}
                      >
                        {isRejecting ? (
                          <div className="weak-candidate-small-spinner weak-candidate-small-spinner--reject"></div>
                        ) : (
                          <Icon name="trash alternate outline" />
                        )}
                      </div>
                    }
                    content="Descartar esta sugerencia"
                    position="top center"
                    size="tiny"
                    inverted
                  />
                )}
              </div>
            </div>
            <div className="weak-candidate-text">
              {name}
            </div>
          </div>
        </div>
      </div>
      <ModalNominaImputation
        open={showNominaModal}
        onClose={() => {
          setShowNominaModal(false)
          setPendingMatchInput(null)
          reconcileClickedRef.current = false
        }}
        onConfirm={handleNominaConfirm}
        document={candidate}
        conflictAmount={pendingMatchInput?.amount ?? 0}
      />
      <ModuleTcStatementPickerModal
        open={tcPicker.open}
        participants={tcPicker.participants}
        onCancel={() => {
          tcPicker.cancel();
          setIsReconciling(false);
          reconcileClickedRef.current = false;
        }}
        onConfirm={handleModuleTcPickerConfirm}
      />
    </>
  );
};

export default WeakCandidate;

